import { useState, useRef } from "react";
import { startAiCompareStream } from "../../../api/compareApi";
import type { Product } from "../../../types/product";

export const useAiCompareSSE = () => {
  const [blocks, setBlocks] = useState<string[]>([]);
  const bufferRef = useRef("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const startCompare = async (
    selectedIds: string[],
    allProducts: Product[]
  ) => {
    abortRef.current?.abort();

    const selectedProducts = allProducts.filter((p) =>
      selectedIds.includes(p.id)
    );

    if (selectedProducts.length < 2) {
      alert("比較する商品を2つ以上選んでください");
      return;
    }

    setBlocks([]);
    bufferRef.current = "";
    setDone(false);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await startAiCompareStream(
        selectedProducts,
        (chunk) => {
          console.log("RAW CHUNK >>>", JSON.stringify(chunk));

          const { text, done } = extractMarkdownFromSse(chunk);

          const { newBlocks, rest } =
            splitByHrIncremental(bufferRef.current, text);

          if (newBlocks.length > 0) {
            setBlocks((prev) => [...prev, ...newBlocks]);
          }

          bufferRef.current = rest;

          if (done) {
            const last = bufferRef.current.trim();
            if (last) {
              setBlocks((prev) => [...prev, last]);
            }
            bufferRef.current = "";
            setLoading(false);
            setDone(true);
          }
        },

        () => {
          // 残りバッファを最後のブロックとして確定
          const last = bufferRef.current.trim();
          if (last) {
            setBlocks((prev) => [...prev, last]);
          }
          bufferRef.current = "";

          setLoading(false);
          setDone(true);
        },
        (e) => {
          console.error("ai-compare error", e);
          setLoading(false);
        }
      );
    } catch (e) {
      console.error("ai-compare failed", e);
      setLoading(false);
    }
  };

  // SSE chunk から Markdown と DONE を取り出す
  function extractMarkdownFromSse(chunk: string) {
    if (chunk === "[DONE]") {
      return { text: "", done: true };
    }

    return {
      text: chunk,
      done: false,
    };
  }


  // --- が来たらブロック確定
  function splitByHrIncremental(buffer: string, incoming: string) {
    const merged = buffer + incoming;

    // 「--- だけの行」で分割
    const parts = merged.split(/^\s*---\s*$/m);

    return {
      newBlocks: parts.slice(0, -1).map(p => p.trim()).filter(Boolean),
      rest: parts[parts.length - 1] ?? "",
    };
  }

  const stopCompare = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
    setDone(false);
  };

  return {
    blocks,
    loading,
    done,
    startCompare,
    stopCompare,
  };
};
